import type { HTMLAttributes } from 'react';
import { cn } from '@/shared/utils/utils';

export type BadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info';

const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-surface-variant text-on-surface-variant ring-1 ring-inset ring-outline',
  primary: 'bg-primary-container text-on-primary-container ring-1 ring-inset ring-primary/20',
  success: 'bg-success/10 text-success-text ring-1 ring-inset ring-success/20',
  warning: 'bg-warning/10 text-warning-text ring-1 ring-inset ring-warning/20',
  danger: 'bg-danger/10 text-danger-text ring-1 ring-inset ring-danger/20',
  info: 'bg-info/10 text-info-text ring-1 ring-inset ring-info/20',
};

/**
 * shadcn-style aliases accepted by `variant`, mapped onto this kit's tones.
 * Pages authored against the other UI kit pass `variant`; both spellings work.
 */
const VARIANT_ALIASES: Record<string, BadgeTone> = {
  default: 'neutral',
  secondary: 'neutral',
  outline: 'neutral',
  destructive: 'danger',
  error: 'danger',
  neutral: 'neutral',
  primary: 'primary',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  info: 'info',
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  /** Alias for `tone`, accepting shadcn variant names. `tone` wins if both set. */
  variant?: BadgeTone | 'default' | 'secondary' | 'outline' | 'destructive' | 'error' | (string & {});
}

export function Badge({ className, tone, variant, ...props }: BadgeProps) {
  const resolved: BadgeTone = tone ?? (variant ? VARIANT_ALIASES[variant] ?? 'neutral' : 'neutral');
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-label-caps uppercase',
        TONES[resolved],
        className,
      )}
      {...props}
    />
  );
}
